(() => {
  'use strict';
  if (customElements.get('kama-v2-preorder')) return;
  const query = `query KamaPreorderContents($handle:String!){product(handle:$handle){id title variants(first:50){nodes{id title availableForSale requiresComponents components(first:50){nodes{quantity productVariant{id title availableForSale currentlyNotInStock product{title handle}}}pageInfo{hasNextPage}}}pageInfo{hasNextPage}}}}`;
  const shortName = name => name.replace(/^Kama Gear\s+/i, '');
  const plainNode = (tag, value) => { const node = document.createElement(tag); node.textContent = value; return node; };

  class KamaV2Preorder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.config = JSON.parse(this.querySelector('[data-po-config]').textContent);
      this.form = this.closest('form');
      this.dialog = this.querySelector('dialog');
      this.status = this.querySelector('[data-po-status]');
      this.content = this.querySelector('[data-po-content]');
      this.confirm = this.querySelector('[data-po-confirm]');
      this.checkbox = this.querySelector('[data-po-accept]');
      this.querySelectorAll('[data-po-close]').forEach(button => button.addEventListener('click', () => this.close()));
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('close', () => this.restoreScroll());
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      });
      this.dialog.addEventListener('change', () => this.updateConfirm());
      this.confirm.addEventListener('click', () => this.accept());
    }

    disconnectedCallback() {
      this.controller?.abort();
      if (this.dialog?.open) this.close();
    }

    variantId() { return String(this.form?.querySelector('[name="id"]')?.value || ''); }
    quantity() { return Number(this.form?.querySelector('[name="quantity"]')?.value || 1); }
    choice() { return this.querySelector('[data-po-delivery]:checked')?.value; }
    updateConfirm() { this.confirm.disabled = !this.checkbox.checked || !this.choice() || !this.selection || this.busy; }

    async review(trigger) {
      if (!this.form || this.dialog.open) return;
      this.trigger = trigger || document.activeElement;
      this.approved = false;
      this.selection = null;
      this.checkbox.checked = false;
      this.querySelectorAll('[data-po-delivery]').forEach(input => { input.checked = false; });
      this.form.querySelectorAll('[data-po-property]').forEach(input => input.remove());
      this.querySelector('[data-po-preview-result]').hidden = true;
      this.content.hidden = true;
      this.status.hidden = false;
      this.status.textContent = 'Checking what’s included and available…';
      this.confirm.disabled = true;
      this.savedOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.querySelector('h2').focus({preventScroll:true});
      const reviewToken = {};
      this.reviewToken = reviewToken;
      const loadingStarted = performance.now();
      const waitForLoading = async () => {
        if (this.reviewToken !== reviewToken || !this.dialog.open) return false;
        const remaining = 2500 - (performance.now() - loadingStarted);
        if (remaining > 0) await new Promise(resolve => setTimeout(resolve, remaining));
        return this.reviewToken === reviewToken && this.dialog.open;
      };
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      this.timeout = timeout;
      try {
        const response = await fetch('/api/2026-10/graphql.json', {
          method:'POST', headers:{'Content-Type':'application/json'},
          body:JSON.stringify({query,variables:{handle:this.dataset.handle}}), signal:controller.signal
        });
        if (!response.ok) throw new Error('Could not check availability');
        const data = await response.json();
        const product = data.data?.product;
        if (data.errors?.length || !product || product.variants.pageInfo.hasNextPage) throw new Error('Incomplete bundle data');
        const variant = product.variants.nodes.find(item => item.id.split('/').pop() === this.variantId());
        if (!variant?.requiresComponents || variant.components.pageInfo.hasNextPage || !variant.components.nodes.length) throw new Error('Incomplete components');
        if (!variant.components.nodes.some(item => item.productVariant.id === this.config.standVariant)) throw new Error('Stand configuration changed');
        if (!(await waitForLoading())) return;
        this.renderSelection(product, variant);
      } catch (error) {
        if (!(await waitForLoading())) return;
        this.status.hidden = false;
        this.status.textContent = 'We couldn’t confirm this bundle’s availability. Please close this box and try again, or contact the KAMA crew before ordering.';
      } finally { clearTimeout(timeout); }
    }

    renderSelection(product, variant) {
      const ready = [], later = [], unavailable = [];
      for (const item of variant.components.nodes) {
        const component = item.productVariant;
        if (component.id === this.config.standVariant) {
          later.push({label:'Stand 2.0',delivery:this.config.standDelivery,kind:'stand'});
        } else if (component.id === this.config.griddleVariant && (!component.availableForSale || component.currentlyNotInStock)) {
          later.push({label:'Half Moon Cast Iron Reversible Griddle',delivery:this.config.griddleDelivery,kind:'griddle'});
        } else if (!component.availableForSale || component.currentlyNotInStock) {
          unavailable.push(shortName(component.product.title));
        } else ready.push(item);
      }
      if (unavailable.length) {
        this.status.textContent = `This selection also has unavailable items: ${unavailable.join(', ')}. Please choose another colour or contact us before ordering.`;
        return;
      }
      this.hasGriddle = later.some(item => item.kind === 'griddle');
      later.sort((a,b) => Number(b.kind === 'stand') - Number(a.kind === 'stand'));
      this.selection = {variantId:this.variantId(),quantity:this.quantity(),variant,later};
      this.querySelector('[data-po-name]').textContent = `${product.title} · ${variant.title}`;
      this.querySelector('[data-po-count]').textContent = `${ready.reduce((sum,item) => sum + item.quantity, 0)} items · view contents`;
      this.querySelector('[data-po-ready]').replaceChildren(...ready.map(item => plainNode('li',`${item.quantity} × ${shortName(item.productVariant.product.title)}${item.productVariant.title === 'Default Title' ? '' : ` — ${item.productVariant.title}`}`)));
      this.querySelector('[data-po-later]').replaceChildren(...later.map(item => {
        const row = document.createElement('li'); row.append(plainNode('strong', item.label),plainNode('span',item.delivery)); return row;
      }));
      this.querySelector('[data-po-split]').textContent = `Your UK-stock items are dispatched first. ${this.hasGriddle ? 'Stand 2.0 is dispatched from our China stock within one working day. Please allow two weeks for delivery; the griddle follows when available.' : 'Stand 2.0 is dispatched from our China stock within one working day. Please allow two weeks for delivery.'} ${this.config.splitDelivery}`;
      this.querySelector('[data-po-together]').textContent = this.hasGriddle
        ? 'We hold the available items and send the complete bundle once Stand 2.0 and the griddle are ready. The griddle delivery date is still to be confirmed.'
        : 'We hold the UK-stock items and send the complete bundle together. Please allow two weeks for delivery.';
      this.termsText = `Payment is taken at checkout. Stand 2.0: ${this.config.standDelivery}.${this.hasGriddle ? ` Griddle: ${this.config.griddleDelivery}. Choosing delivery together means waiting until all items are ready; the complete bundle therefore has no confirmed delivery date yet.` : ''} ${this.config.splitDelivery}`;
      this.querySelector('[data-po-terms]').textContent = `Payment is taken at checkout.${this.hasGriddle ? ' If you choose delivery together, there is no confirmed delivery date for the complete bundle until the griddle date is known.' : ''} Your delivery choice is saved with your order.`;
      this.status.hidden = true;
      this.content.hidden = false;
      this.confirm.querySelector('span').textContent = this.config.mode === 'preview' ? 'Accept & preview confirmation' : 'Accept & add to cart';
      this.updateConfirm();
    }

    properties() {
      const properties = {
        'Delivery preference': this.choice() === 'split' ? 'Send UK-stock items first; please allow two weeks for Stand 2.0 delivery' : 'Hold and send the complete bundle together',
        'Stand 2.0 delivery':this.config.standDelivery,
        'Delivery terms accepted':'Yes',
        '_kama_preorder_terms':this.config.termsVersion,
        '_kama_preorder_terms_text':this.termsText,
        '_kama_preorder_accepted_at':new Date().toISOString()
      };
      if (this.hasGriddle) properties['Griddle delivery'] = this.config.griddleDelivery;
      if (this.choice() === 'split') properties['Split delivery'] = this.config.splitDelivery;
      return properties;
    }

    async accept() {
      if (!this.checkbox.checked || !this.choice() || !this.selection || this.busy) return;
      if (this.selection.variantId !== this.variantId() || this.selection.quantity !== this.quantity()) {
        this.status.hidden = false; this.status.textContent = 'Your selection changed. Close this box and review the delivery details again.';
        this.confirm.disabled = true; this.selection = null; return;
      }
      const properties = this.properties();
      if (this.config.mode === 'preview') {
        const rows = Object.entries(properties).filter(([name]) => !name.startsWith('_')).map(([name,value]) => {
          const row = document.createElement('div'); row.className = 'kgpo__result-row'; row.append(plainNode('dt',name),plainNode('dd',value)); return row;
        });
        this.querySelector('[data-po-result-details]').replaceChildren(...rows);
        this.content.hidden = true;
        const result = this.querySelector('[data-po-preview-result]'); result.hidden = false; result.focus({preventScroll:true});
        this.dialog.scrollTop = 0;
        return;
      }
      if (this.config.mode !== 'active' || !this.selection.variant.availableForSale) {
        this.status.hidden = false; this.status.textContent = 'This selection is not currently available to order. No items have been added to your cart.'; this.confirm.disabled = true; return;
      }
      this.form.querySelectorAll('[data-po-property]').forEach(input => input.remove());
      for (const [name,value] of Object.entries(properties)) {
        const input = document.createElement('input'); input.type = 'hidden'; input.name = `properties[${name}]`; input.value = value; input.dataset.poProperty = ''; this.form.appendChild(input);
      }
      this.approved = {variantId:this.variantId(),quantity:this.quantity()};
      this.close();
      const submitter = this.form.querySelector('[name="add"]');
      if (!submitter || submitter.disabled) { this.approved = false; return; }
      this.form.requestSubmit(submitter);
    }

    restoreScroll() { if (this.savedOverflow !== undefined) {document.body.style.overflow = this.savedOverflow; this.savedOverflow = undefined;} }
    close() { this.reviewToken = null; this.controller?.abort(); clearTimeout(this.timeout); this.dialog.close(); this.restoreScroll(); if (this.trigger?.isConnected) this.trigger.focus({preventScroll:true}); }
  }
  customElements.define('kama-v2-preorder',KamaV2Preorder);
  window.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[data-kama-preorder-trigger], form[data-type="add-to-cart-form"] [name="add"], sticky-add-to-cart .sticky-add-to-cart__button');
    if (!button || button.disabled) return;
    const form = button.closest('form');
    const control = form?.querySelector('kama-v2-preorder') || (button.closest('sticky-add-to-cart') ? document.querySelector('#MainContent kama-v2-preorder') : null);
    if (!control) return;
    event.preventDefault(); event.stopImmediatePropagation(); control.review(button);
  },true);
  window.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const control = form.querySelector('kama-v2-preorder');
    if (!control) return;
    if (control.approved && control.approved.variantId === control.variantId() && control.approved.quantity === control.quantity()) {
      control.approved = false; return;
    }
    event.preventDefault(); event.stopImmediatePropagation(); control.review(event.submitter);
  },true);
})();
